import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Member, MemberListItem} from "@/models/member.ts";
import ValidationAlert from "@/components/validation-alert.tsx";
import {Field, Label} from "@/components/tailwind/fieldset.tsx";
import {Input} from "@/components/tailwind/input.tsx";
import {Select} from "@/components/tailwind/select.tsx";
import {useEffect, useMemo, useState} from "react";
import {useAuth} from "@/context/use-auth.tsx";
import {ResignationReason} from "@/models/resignation-reason.ts";
import {fetchAllResignationReasons} from "@/services/resignation-reason-services.ts";
import {getAllMembers, generateCompleteMemberName, ResignationPointsTarget} from "@/services/member-services.ts";
import {getSocialAccountEnabled} from "@/services/social-account-settings-services.ts";
import MemberSelectDialog from "@/components/member-select-dialog.tsx";
import log from "@/lib/logger";

interface MemberCancellationDialogProps {
    isOpen: boolean;
    onConfirm: (
        cancellationDate: string,
        resignationReasonId: string,
        pointsTarget?: ResignationPointsTarget,
        pointsTargetMemberId?: string
    ) => void;
    onClose: () => void;
    memberData: Member;
}

function isValidDate(dateStr: string): boolean {
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
}

const memberCancellationSchema = z.object({
    cancellationDate: z.string()
        .refine((value) => Boolean(value) && isValidDate(value), {
            message: "memberCancellation.errors.invalid-date-format"
        })
        .refine((value) => value !== undefined && value !== null && value !== "", {
            message: "memberCancellation.errors.date-required"
        }),
    resignationReasonId: z.string().min(1, {
        message: "memberCancellation.errors.reason-required"
    })
});

export type MemberCancellationFormData = z.infer<typeof memberCancellationSchema>;

export default function MemberCancellationDialog({
    isOpen,
    onConfirm,
    onClose,
    memberData
}: MemberCancellationDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth();

    const [reasons, setReasons] = useState<ResignationReason[]>([]);
    const [socialAccountEnabled, setSocialAccountEnabled] = useState(false);
    const [members, setMembers] = useState<MemberListItem[]>([]);
    const [pointsTarget, setPointsTarget] = useState<ResignationPointsTarget | "">("");
    const [pointsTargetMemberId, setPointsTargetMemberId] = useState<string | null>(null);
    const [selectedMemberName, setSelectedMemberName] = useState("");
    const [pointsTargetError, setPointsTargetError] = useState("");
    const [isMemberSelectOpen, setIsMemberSelectOpen] = useState(false);

    const today = new Date().toISOString().split('T')[0];
    const defaultValues = useMemo(() => ({
        cancellationDate: today,
        resignationReasonId: ""
    }), [today]);

    const {
        handleSubmit,
        setError,
        control,
        formState: {errors},
        reset
    } = useForm<MemberCancellationFormData>({
        resolver: zodResolver(memberCancellationSchema),
        defaultValues
    });

    useEffect(() => {
        if (isOpen) {
            reset(defaultValues);
            setPointsTarget("");
            setPointsTargetMemberId(null);
            setSelectedMemberName("");
            setPointsTargetError("");
            setMembers([]);
            setSocialAccountEnabled(false);
            fetchAllResignationReasons(token)
                .then((fetched) => {
                    if (fetched) {
                        setReasons(fetched.sort((a, b) => a.sortOrder - b.sortOrder));
                    }
                })
                .catch((error) => log.error("Unable to load resignation reasons.", error));
            getSocialAccountEnabled(token ?? "")
                .then((enabled) => {
                    setSocialAccountEnabled(enabled);
                    if (enabled) {
                        // A late response must not override an explicit choice.
                        setPointsTarget((current) => (current === "" ? "SOCIAL_ACCOUNT" : current));
                    }
                })
                .catch(() => setSocialAccountEnabled(false));
            getAllMembers(token)
                .then((fetched) => setMembers(fetched ?? []))
                .catch((error) => log.error("Unable to load members.", error));
        }
    }, [isOpen, reset, defaultValues, token]);

    async function onSubmit(data: MemberCancellationFormData) {
        const cancellationDate = new Date(data.cancellationDate);
        const membershipStartDate = new Date(memberData.membershipStartDate!);

        if (cancellationDate < membershipStartDate) {
            setError("cancellationDate", {
                type: "manual",
                message: "memberCancellation.errors.date-before-membership-start"
            });
            return;
        }

        if (!pointsTarget) {
            setPointsTargetError("memberCancellation.errors.points-target-required");
            return;
        }
        if (pointsTarget === "MEMBER" && !pointsTargetMemberId) {
            setPointsTargetError("memberCancellation.errors.points-target-member-required");
            return;
        }

        onConfirm(
            data.cancellationDate,
            data.resignationReasonId,
            pointsTarget,
            pointsTarget === "MEMBER" ? (pointsTargetMemberId ?? undefined) : undefined
        );
        handleClosing();
    }

    function handleClosing() {
        reset();
        onClose();
    }

    function handleMemberSelected(member: MemberListItem) {
        setPointsTargetMemberId(member.id);
        setSelectedMemberName(generateCompleteMemberName(member));
        setPointsTargetError("");
        setIsMemberSelectOpen(false);
    }

    return (
        <Dialog open={isOpen} onClose={handleClosing}>
            <DialogTitle>
                {t('memberCancellation.title')}
            </DialogTitle>
            <DialogDescription>
                {t('memberCancellation.description')}
            </DialogDescription>

            <DialogBody>
                {errors.root && (
                    <ValidationAlert message={errors.root?.message || tCommon("serverError.unknown")}/>
                )}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <Field>
                            <Label htmlFor="cancellation-date">
                                {t('memberCancellation.fields.cancellationDate.label')}
                            </Label>
                            <Controller
                                name="cancellationDate"
                                control={control}
                                render={({field}) => (
                                    <Input
                                        id="cancellation-date"
                                        type="date"
                                        value={field.value || ''}
                                        min={memberData.membershipStartDate}
                                        onChange={(e) => {
                                            const value = e.target.value || null;
                                            field.onChange(value);
                                        }}
                                        aria-label={t('memberCancellation.fields.cancellationDate.ariaLabel')}
                                        invalid={Boolean(errors.cancellationDate)}
                                        data-testid="cancellation-date-input"
                                    />
                                )}
                            />
                        </Field>
                        {errors.cancellationDate && (
                            <ValidationAlert
                                message={t(errors.cancellationDate?.message || "memberCancellation.errors.unknown-issue")}
                            />
                        )}
                    </div>

                    <div>
                        <Field>
                            <Label htmlFor="resignation-reason">
                                {t('memberCancellation.fields.resignationReason.label')}
                            </Label>
                            <Controller
                                name="resignationReasonId"
                                control={control}
                                render={({field}) => (
                                    <Select
                                        id="resignation-reason"
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        aria-label={t('memberCancellation.fields.resignationReason.ariaLabel')}
                                        invalid={Boolean(errors.resignationReasonId)}
                                        data-testid="resignation-reason-select"
                                    >
                                        <option value="" disabled>
                                            {t('memberCancellation.fields.resignationReason.placeholder')}
                                        </option>
                                        {reasons.map((reason) => (
                                            <option key={reason.id} value={reason.id}>
                                                {reason.name}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </Field>
                        {errors.resignationReasonId && (
                            <ValidationAlert
                                message={t(errors.resignationReasonId?.message || "memberCancellation.errors.unknown-issue")}
                            />
                        )}
                    </div>

                    <div>
                        <Field>
                            <Label htmlFor="points-target">
                                {t('memberCancellation.fields.pointsTarget.label')}
                            </Label>
                            <Select
                                id="points-target"
                                value={pointsTarget}
                                onChange={(e) => {
                                    setPointsTarget(e.target.value as ResignationPointsTarget | "");
                                    setPointsTargetError("");
                                }}
                                aria-label={t('memberCancellation.fields.pointsTarget.ariaLabel')}
                                invalid={Boolean(pointsTargetError)}
                                data-testid="points-target-select"
                            >
                                <option value="" disabled>
                                    {t('memberCancellation.fields.pointsTarget.placeholder')}
                                </option>
                                {socialAccountEnabled && (
                                    <option value="SOCIAL_ACCOUNT">
                                        {t('memberCancellation.fields.pointsTarget.options.social-account')}
                                    </option>
                                )}
                                <option value="MEMBER">
                                    {t('memberCancellation.fields.pointsTarget.options.member')}
                                </option>
                                <option value="LAPSE">
                                    {t('memberCancellation.fields.pointsTarget.options.lapse')}
                                </option>
                            </Select>
                        </Field>

                        {pointsTarget === "MEMBER" && (
                            <div className="mt-2">
                                <Button
                                    type="button"
                                    plain
                                    onClick={() => setIsMemberSelectOpen(true)}
                                    data-testid="points-target-member-button"
                                >
                                    {t('memberCancellation.fields.pointsTargetMember.select-button')}
                                </Button>
                                <span className="ml-2 text-sm text-zinc-600 dark:text-zinc-400"
                                      data-testid="points-target-member-name">
                                    {selectedMemberName || t('memberCancellation.fields.pointsTargetMember.none-selected')}
                                </span>
                            </div>
                        )}

                        {pointsTargetError && (
                            <ValidationAlert message={t(pointsTargetError)}/>
                        )}
                    </div>

                    <DialogActions>
                        <Button
                            type="button"
                            plain
                            onClick={handleClosing}
                            aria-label={tCommon('buttons.cancel.ariaLabel')}
                            data-testid="cancel-cancellation-button"
                        >
                            {tCommon('buttons.cancel.text')}
                        </Button>
                        <Button
                            type="submit"
                            data-testid="confirm-cancellation-button"
                            aria-label={t('memberCancellation.buttons.confirm.ariaLabel')}
                        >
                            {t('memberCancellation.buttons.confirm.text')}
                        </Button>
                    </DialogActions>
                </form>
            </DialogBody>

            <MemberSelectDialog
                isOpen={isMemberSelectOpen}
                onClose={() => setIsMemberSelectOpen(false)}
                onSelect={handleMemberSelected}
                members={members}
                title={t('memberCancellation.fields.pointsTargetMember.dialog-title')}
                excludeMemberId={memberData.id}
            />
        </Dialog>
    );
}
